import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Phone, Globe2 } from "lucide-react";
import { brand } from "@/data/site/brand";
import { primaryMetros, headquarters } from "@/data/site/serviceAreas";

interface Props {
  onClose: () => void;
}

export const SiteServiceAreasDropdown = ({ onClose }: Props) => {
  const metros = primaryMetros.filter((m) => m.content);

  return (
    <div className="bg-white border-b border-border shadow-lg">
      <div className="container mx-auto px-6 py-8">
        <div className="grid grid-cols-12 gap-8">
          {/* Nationwide feature card */}
          <div className="col-span-5 group relative overflow-hidden rounded-md bg-gradient-navy text-white p-6 flex flex-col justify-between hover:shadow-xl transition-shadow">
            <Link
              to="/service-areas"
              onClick={onClose}
              className="block"
            >
              <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-white/80 mb-3">
                <Globe2 className="w-3 h-3" /> Nationwide Representation
              </span>
              <h3 className="font-heading text-2xl font-bold leading-tight">
                We represent taxpayers in <span className="text-white font-semibold underline decoration-gold decoration-2 underline-offset-4">all 50 states.</span>
              </h3>
              <p className="text-[13px] text-white/85 mt-3 leading-snug">
                CPAs are federally authorized to handle IRS matters anywhere in the U.S.
                Most clients work with us remotely by phone, video, and secure portal —
                you don't need to be local to hire us.
              </p>
              <p className="text-[11px] text-white/60 mt-3">
                Headquartered in {headquarters.city}, {headquarters.state} · {headquarters.region}
              </p>
            </Link>
            <div className="mt-5 flex items-center justify-between gap-3">
              <a
                href={`tel:${brand.phoneTel}`}
                className="inline-flex items-center gap-2 bg-gold hover:bg-gold-dark text-navy text-sm font-bold px-3.5 py-2 rounded transition-colors"
              >
                <Phone className="w-3.5 h-3.5" /> {brand.phone}
              </a>
              <Link
                to="/service-areas"
                onClick={onClose}
                className="inline-flex items-center gap-1 text-xs font-semibold text-white/90 hover:text-gold hover:gap-2 transition-all"
              >
                All service areas <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>

          {/* Metro grid */}
          <div className="col-span-7">
            <div className="flex items-baseline justify-between">
              <span className="text-[11px] font-bold text-navy/60 uppercase tracking-[0.18em]">
                Featured Local Metros
              </span>
              <span className="text-[11px] text-muted-foreground italic">
                Not local? We still represent you.
              </span>
            </div>
            <ul className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1">
              {metros.map((m) => (
                <li key={m.slug}>
                  <Link
                    to={`/service-areas/${m.slug}`}
                    onClick={onClose}
                    className="group flex items-start gap-3 py-2.5 px-2 -mx-2 rounded transition-colors hover:bg-secondary/60"
                  >
                    <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded bg-secondary text-navy group-hover:bg-gold group-hover:text-navy-dark transition-colors">
                      <MapPin className="w-4 h-4" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-navy leading-tight">
                        {m.city}, {m.state}
                      </span>
                      <span className="block text-xs text-muted-foreground mt-0.5 leading-snug line-clamp-1">
                        {m.blurb}
                      </span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Footer strip */}
        <div className="mt-6 pt-5 border-t border-border flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-muted-foreground">
            <span><strong className="text-navy">All 50 states</strong> — CPA federally authorized</span>
            <span><strong className="text-navy">Remote</strong> by phone, video & secure portal</span>
          </div>
          <Link
            to="/service-areas"
            onClick={onClose}
            className="inline-flex items-center gap-1 text-sm font-bold text-navy hover:text-gold transition-colors"
          >
            View all service areas <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </div>
  );
};
